import { Injectable } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { ICellRendererParams } from 'ag-grid-community';
import { Observable, of } from 'rxjs';
import { flatMap, tap } from 'rxjs/operators';

import {
  BASIC_ACCESS_MODAL_DATA,
  BASIC_CHANGE_LAYOUT_MODAL_DATA,
  BASIC_ELIGIBILITY_MODAL_DATA,
  BASIC_RECALL_MODAL_DATA,
  BASIC_REOPEN_MODAL_DATA,
  BASIC_SUBMIT_MODAL_DATA,
  BASIC_VALIDATION_MODAL_DATA,
  EMPTY_DEFAULT_NOTIFICATIONS_DATA,
  ModalConfig,
  NOTIFICATIONS_WITHOUT_MESSAGE_MODAL_DATA,
  NOTIFICATIONS_WITHOUT_NOTIFICATION_MODAL_DATA,
  SUBMIT_CUSTOM_DEADLINE_MODAL_DATA,
  SUBMIT_CUSTOM_NOTIFICATIONS_MODAL_DATA,
  SUBMIT_EMPTY_NOTIFICATIONS,
  UNSAVED_TIMESHEET
} from '../constants/modal.constants';
import { TimesheetService } from '@app/timesheet-user/services/timesheet.service';
import { EmployeesService } from '@app/employees/services/employees.service';
import { TimesheetDOS } from '@app/timesheet-user/services/timesheet-dos.service';
import { ModalTimesheetDosService } from './modal-timesheet-dos.service';
import { AbstractSubscriptionManager } from '../classes/subscription-manager';
import { UserTimesheet } from '@app/timesheet-user/models/user-timesheet.interface';
import { UserEntitlementsData } from '@app/models/row-data.interface';
import { ModalData } from '@app/shared/models/modal-data';
import { TimesheetStatus } from '../constants/timesheet.constants';
import { CustomDeadlineDOS } from '@settings/components/custom-deadline/services/custom-deadline-dos.service';
import { CustomDeadlineService } from '@settings/components/custom-deadline/services/custom-deadline.service';
import { IMessage, INotificationSplit, NotificationType } from '@app/settings/models';
import { CustomNotificationsService } from '@app/settings/components/custom-notifications/services/custom-notifications.service';
import { DefaultNotificationsService } from '@app/settings/components/default-notifications/services/default-notifications.service';
import { MonthPickerService } from '@app/shared/components/month-picker/month-picker.service';

@Injectable({
  providedIn: 'root'
})
export class ModalService extends AbstractSubscriptionManager {
  modalConfig = new MatDialogConfig();

  constructor(
    private _timesheetDOS: TimesheetDOS,
    private _timesheetService: TimesheetService,
    private _employeesService: EmployeesService,
    private _modalTimesheetDosService: ModalTimesheetDosService,
    private _customDeadlineDOS: CustomDeadlineDOS,
    private _customDeadlineService: CustomDeadlineService,
    private _customNotificationsService: CustomNotificationsService,
    private _defaultNotificationsService: DefaultNotificationsService,
    private _monthPickerService: MonthPickerService
  ) {
    super();
    this.modalConfig.disableClose = true;
    this.modalConfig.height = ModalConfig.AutoHeight;
    this.modalConfig.width = ModalConfig.StandardWidth;
  }

  createConfirmModalData(data: UserTimesheet): MatDialogConfig {
    const confirmModalData = {
      ...BASIC_SUBMIT_MODAL_DATA
    } as ModalData;

    return {
      ...this.modalConfig,
      data: confirmModalData
    };
  }

  createReopenModalData(data: UserTimesheet): MatDialogConfig {
    const reopenModalData = {
      ...BASIC_REOPEN_MODAL_DATA,
      fullName: data.fullName
    } as ModalData;

    return {
      ...this.modalConfig,
      height: ModalConfig.AutoHeight,
      data: reopenModalData
    };
  }

  createAccessModalData(params: ICellRendererParams): MatDialogConfig {
    const accessModalData = {
      ...BASIC_ACCESS_MODAL_DATA,
      fullName: params.data.fullName,
      userStatus: params.data.accessSettings,
      username: params.data.username
    } as ModalData;

    return {
      ...this.modalConfig,
      data: accessModalData
    };
  }

  createRecallModalData(): MatDialogConfig {
    const recallModalData = {
      ...BASIC_RECALL_MODAL_DATA
    } as ModalData;
    return {
      ...this.modalConfig,
      data: recallModalData
    };
  }

  createLayoutModalData(): MatDialogConfig {
    const layoutModalData = {
      ...BASIC_CHANGE_LAYOUT_MODAL_DATA
    } as ModalData;

    return {
      ...this.modalConfig,
      height: ModalConfig.AutoHeight,
      width: ModalConfig.SmallerWidth,
      data: layoutModalData
    };
  }

  createEligibilityModalData(params: UserEntitlementsData): MatDialogConfig {
    const eligibilityModalData = {
      ...BASIC_ELIGIBILITY_MODAL_DATA,
      username: params.username,
      fullName: params.fullName
    } as ModalData;

    return {
      ...this.modalConfig,
      height: ModalConfig.AutoHeight,
      data: eligibilityModalData
    };
  }

  createCustomDeadlineModalData(selectedMonth: Date): MatDialogConfig {
    const customDeadlineModalData = {
      ...SUBMIT_CUSTOM_DEADLINE_MODAL_DATA,
      selectedMonth
    } as ModalData;

    return {
      ...this.modalConfig,
      height: ModalConfig.AutoHeight,
      data: customDeadlineModalData
    };
  }

  createCustomNotificationsModalData(
    selectedMonth: Date,
    notifications: INotificationSplit[],
    messages: IMessage[],
    setDefault: boolean,
    nextMonth: Date
  ): MatDialogConfig {
    const payload = {
      notifications,
      messages,
      setDefault,
      nextMonth
    };
    const customNotificationsModalData = {
      ...SUBMIT_CUSTOM_NOTIFICATIONS_MODAL_DATA,
      selectedMonth,
      payload
    } as ModalData;

    return {
      ...this.modalConfig,
      height: ModalConfig.AutoHeight,
      data: customNotificationsModalData
    };
  }

  submitEmptyCustomNotificationsModalData(
    selectedMonth: Date,
    notifications: INotificationSplit[],
    messages: IMessage[],
    setDefault: boolean,
    types: NotificationType[]
  ): MatDialogConfig {
    const month = selectedMonth.toLocaleString('en', { month: 'long' });
    const year = selectedMonth.getFullYear();
    const payload = {
      notifications,
      messages,
      setDefault,
      nextMonth: selectedMonth
    };
    const emptyCustomNotificationsModalData = {
      ...SUBMIT_EMPTY_NOTIFICATIONS,
      selectedMonth,
      text: `There are no notifications set for ${month} ${year}. Please click Restore to Default Notifications if you would like to revert to default state.`,
      payload,
      types
    };

    return {
      ...this.modalConfig,
      height: ModalConfig.AutoHeight,
      data: emptyCustomNotificationsModalData
    };
  }

  createNotificationsWithoutMessageModalData(): MatDialogConfig {
    const notificationsWithoutMessageModalData = {
      ...NOTIFICATIONS_WITHOUT_MESSAGE_MODAL_DATA
    } as ModalData;

    return {
      ...this.modalConfig,
      data: notificationsWithoutMessageModalData
    };
  }

  createNotificationsWithoutNotificationsModalData(): MatDialogConfig {
    const notificationsWithoutNotificationsModalData = {
      ...NOTIFICATIONS_WITHOUT_NOTIFICATION_MODAL_DATA
    } as ModalData;

    return {
      ...this.modalConfig,
      data: notificationsWithoutNotificationsModalData
    };
  }

  createValidationModalData(): MatDialogConfig {
    const validationModalData = {
      ...BASIC_VALIDATION_MODAL_DATA
    } as ModalData;

    return {
      ...this.modalConfig,
      width: ModalConfig.SmallerWidth,
      data: validationModalData
    };
  }

  createEmptyDefaultNotifications(selectedMonth: Date, types: NotificationType[]): MatDialogConfig {
    const monthName = new Date().toLocaleDateString('en', { month: 'long' });
    const year = new Date().getFullYear();

    const text = `${EMPTY_DEFAULT_NOTIFICATIONS_DATA.text} ${monthName} ${year}?`;
    const successMessage = `${EMPTY_DEFAULT_NOTIFICATIONS_DATA.successMessage} ${monthName} ${year}`;
    const emptyDefaultNotificationsModalData = {
      ...EMPTY_DEFAULT_NOTIFICATIONS_DATA,
      text,
      selectedMonth,
      successMessage,
      types
    } as ModalData;

    return {
      ...this.modalConfig,
      data: emptyDefaultNotificationsModalData
    };
  }

  createUnsavedTimesheetData(
    parentComponent: string,
    currentMonth: Date,
    selectedDate: Date,
    actionType: string
  ): MatDialogConfig {
    const timesheetData = {
      parentComponent,
      currentMonth,
      selectedDate,
      actionType
    };

    const unsavedTimesheetModalData = {
      ...UNSAVED_TIMESHEET,
      timesheetData
    } as ModalData;

    return {
      ...this.modalConfig,
      data: unsavedTimesheetModalData
    };
  }

  handleModalAction(
    modalData: ModalData
  ): Observable<UserEntitlementsData | UserTimesheet | TimesheetStatus | UserEntitlementsData | void> {
    switch (modalData.name) {
      case ModalConfig.Submit:
        return this._onSaveAndSubmitTimesheet();
      case ModalConfig.ChangeAccess:
        return this._changeAccess(modalData.userStatus, modalData.username);
      case ModalConfig.Recall:
        return this._recallTimesheet();
      case ModalConfig.Reopen:
        return this._reopenTimesheet();
      case ModalConfig.CustomDeadline:
        return this._saveModifiedCustomDeadline();
      case ModalConfig.CustomNotifications:
        return this._saveModifiedCustomNotification(modalData.selectedMonth, modalData.payload, modalData.types);
      case ModalConfig.EmptyCustomNotifications:
        return this._saveModifiedCustomNotification(modalData.selectedMonth, modalData.payload, modalData.types);
      case ModalConfig.EmptyDefaultNotifications:
        return this._saveEmptyDefaultNotifications(modalData);
      case ModalConfig.UnsavedTimesheet:
        const { parentComponent, currentMonth, selectedDate, actionType, isTimesheetValid } = modalData.timesheetData;
        return this._changeUnsavedTimesheet(parentComponent, currentMonth, selectedDate, actionType);
      default:
        throw new Error('Modal not recognized');
    }
  }

  private _onSaveAndSubmitTimesheet(): Observable<undefined> {
    this._timesheetService.onSuccessfulTimesheetSaveAction();
    return this._timesheetService.updateTimesheetData().pipe(
      tap((data) => {
        this._timesheetService.timesheetId = data.timesheetId;
        this._timesheetDOS.updateTimesheetData(data);
      }),
      flatMap(() => this._timesheetService.submitTimesheetStatus())
    );
  }

  private _recallTimesheet(): Observable<UserTimesheet> {
    return this._timesheetService.recallTimesheet().pipe(
      tap((timesheetStatus) => this._timesheetDOS.updateTimesheetStatus(timesheetStatus)),
      flatMap(() => this._timesheetService.loadTimesheet())
    );
  }

  private _changeAccess(role: string, username: string): Observable<UserEntitlementsData> {
    this._modalTimesheetDosService.changeAccessLevel(true);
    return this._employeesService.updateEmployeeAccessLevel(role, username);
  }

  private _reopenTimesheet(): Observable<UserTimesheet> {
    return this._timesheetService
      .reopenTimesheet()
      .pipe(tap(() => this._timesheetDOS.updateTimesheetStatus(TimesheetStatus.Open)));
  }

  private _saveModifiedCustomDeadline(): Observable<undefined> {
    return this._customDeadlineService.patchCustomDeadline({
      selectedMonth: this._customDeadlineDOS.getSelectedMonthSnapshot(),
      ...this._customDeadlineDOS.getCustomDataModifiedSnapshot()
    });
  }

  private _saveModifiedCustomNotification(
    selectedMonth: Date,
    payload: {
      notifications: INotificationSplit[];
      messages: IMessage[];
      setDefault: boolean;
    },
    types: NotificationType[]
  ): Observable<undefined> {
    return this._customNotificationsService.postCustomNotifications(
      selectedMonth,
      payload.notifications,
      payload.messages,
      payload.setDefault,
      types
    );
  }

  private _saveEmptyDefaultNotifications(modalData: ModalData): Observable<undefined> {
    return this._defaultNotificationsService.patchDefaultNotifications(
      [],
      [
        { subject: '', text: '', notificationType: NotificationType.DEADLINE },
        { subject: '', text: '', notificationType: NotificationType.OVERVIEW }
      ],
      modalData.types
    );
  }

  private _changeUnsavedTimesheet(
    parentComponent: string,
    currentMonth: Date,
    selectedDate: Date,
    actionType: string
  ): Observable<UserTimesheet> {
    this._timesheetDOS.updateValidationInProgress(true);
    const isTimesheetValid = !this._timesheetDOS.getValidationErrorsSnapshot();

    if (isTimesheetValid) {
      return this._timesheetService.updateTimesheetData().pipe(
        tap(() => {
          this._timesheetDOS.updateUserTimesheetChangedStatus(false);
          this._monthPickerService.setNewMonth(parentComponent, currentMonth, actionType, selectedDate);
        })
      );
    }

    return of(null);
  }
}
